import { DataPoint } from "@features/analysis/analysisChart/DualLineChart";
import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from 'd3';

interface Series {
  label : string,
  color : string;
  data : DataPoint[];
}

interface Props {
  series: Series[];
  xLabel? : string;
  yLabel? : string;
  width: number;
  height: number;
}

export function MultiSeriesChart({ series, xLabel = "X", yLabel = "Y", width, height }: Props) {
  const mergedData = series[0]?.data.map((_, idx) => {
    const row: Record<string, any> = { x: series[0].data[idx].x };
    series.forEach((s) => {
      row[s.label] = s.data[idx]?.y ?? null;
    });
    return row;
  });

  const svgRef = useRef<SVGSVGElement | null>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const margin = { top: 20, right: 30, bottom: 50, left: 70 };
  const innerWidth = useMemo(() => width - margin.left - margin.right, [width]);
  const innerHeight = useMemo(
    () => height - margin.top - margin.bottom,
    [height],
  );

  const xExtent = useMemo(() => {
    const allX = series.flatMap((s) => s.data.map((d) => +d.x));
    return d3.extent(allX) as [number, number];
  }, [series]);

  const yTicks = useMemo(() => {
    const allY = series
      .flatMap((s) => s.data.map((d) => d.y))
      .filter((value) => Number.isFinite(value));

    if (allY.length === 0 || innerHeight <= 0) {
      return {
        domain: [0, 1] as [number, number],
        values: [0, 1],
      };
    }

    let min = Math.min(...allY);
    let max = Math.max(...allY);

    if (min === max) {
      const padding = Math.max(Math.abs(min) * 0.1, 1);
      min -= padding;
      max += padding;
    }

    const tickCount = Math.max(2, Math.floor(innerHeight / 40));
    const step = d3.tickStep(min, max, tickCount);
    const domainMin = Math.floor(min / step) * step;
    const domainMax = Math.ceil(max / step) * step;
    const values = d3.range(domainMin, domainMax + step * 0.5, step);

    return {
      domain: [domainMin, domainMax] as [number, number],
      values,
    };
  }, [series, innerHeight]);

  const xScale = useMemo(
    () => d3.scaleLinear().domain(xExtent).range([0, innerWidth]),
    [xExtent, innerWidth],
  );

  const yScale = useMemo(
    () => d3.scaleLinear().domain(yTicks.domain).range([innerHeight, 0]),
    [yTicks.domain, innerHeight],
  );

  const xValues = useMemo(() => {
    const vals = series[0]?.data.map(d => d.x) ?? [];
    if (vals.length <= 11) return vals;
    const step = Math.ceil(vals.length / 11)
    return vals.filter((_, i) => i % step === 0).slice(0, 11);
  }, [series]);

  const xAxis = useMemo(
    () =>
      d3.axisBottom(xScale)
        .tickValues(xValues)
        .tickFormat((d) => `${d}%`)
        .tickSizeOuter(0),
    [xScale, xValues],
  );

  const yAxis = useMemo(
    () =>
      d3.axisLeft(yScale)
        .tickValues(yTicks.values)
        .tickFormat((d) => d3.format('~g')(Number(d))),
    [yScale, yTicks.values],
  );

  const draw = () => {
    if (!mergedData) return;

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr(
        'style',
        `max-width:100%; height:auto; height:intrinsic;`,
      );
    svg.selectAll('*').remove();

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    gRef.current = g.node();

    // X축 + X축 그리드
    g.append('g')
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .call(g => g.select(".domain").remove())

      .call(g => {
        g.selectAll('.tick line')
          .attr('stroke', '#0D0D0D')
          .attr('stroke-dasharray', '4 2');

        g.selectAll('.tick text')
          .attr('fill', 'var(--text-default)')
          .attr('font-size', '0.75rem');
      });
    g.append('g')
      .attr('class', 'x-grid')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(xScale)
          .tickSize(-innerHeight)
          .tickFormat(() => "")
      )
      .call(g => g.select(".domain").remove())
      .call(g => {
        g.selectAll(".tick line")
          .attr("stroke", '#424242')
          .attr("stroke-dasharray", "4 2");

        g.select(".tick:first-of-type line").remove();
        g.select(".tick:last-of-type line").remove();
      });

    // Y축 + Y축 그리드
    g.append('g')
    g.append('g')
      .call(yAxis)
      .call(g => g.select(".domain").remove())
      .call(g => {
        g.selectAll('.tick line')
          .attr('stroke', '#0D0D0D')
          .attr('stroke-dasharray', '4 2');

        g.selectAll('.tick text')
          .attr('fill', 'var(--text-default)')
          .attr('font-size', '0.75rem');
      });
    g.append('g')
      .attr('class', 'y-grid')
      .call(
        d3.axisLeft(yScale)
          .tickValues(yTicks.values)
          .tickSize(-innerWidth)
          .tickFormat(() => "")
      )
      .call(g => g.select(".domain").remove())
      .call(g => {
        g.selectAll(".tick line")
          .attr("stroke", '#424242')
          .attr("stroke-dasharray", "4 2");

        g.select(".tick:first-of-type line").remove();
      });


    g.append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#424242')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4 2');
    g.append('line')
      .attr('x1', innerWidth)
      .attr('x2', innerWidth)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', '#424242')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4 2');
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', innerHeight)
      .attr('y2', innerHeight)
      .attr('stroke', '#424242')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4 2');

    series.forEach(s => {
      const line = d3.line<DataPoint>()
        .x(d => xScale(+d.x))
        .y(d => yScale(d.y))
        .curve(d3.curveMonotoneX);

      g.append("path")
        .datum(s.data)
        .attr("fill", "none")
        .attr("stroke", s.color)
        .attr("stroke-width", 2)
        .attr("d", line);

      g.selectAll(`.dot-${s.label}`)
        .data(s.data)
        .enter()
        .append("circle")
        .attr("class", `dot-${s.label}`)
        .attr("cx", d => xScale(+d.x))
        .attr("cy", d => yScale(d.y))
        .attr("r", 4)
        .attr("fill", s.color);
    });

    d3.select(svgRef.current)
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', 18)
      .attr('fill', 'var(--text-default)')
      .attr('text-anchor', 'middle')
      .text(yLabel);
      const legend = d3.select(svgRef.current)
      .append("g")
      .attr("class", "legend")
      .attr("transform", `translate(${width / 2}, ${height - 15})`);

    series.forEach((s, i) => {
      const legendItem = legend.append("g")
        .attr("transform", `translate(${i * 120 - (series.length-1)*75}, 0)`);

      legendItem.append("circle")
        .attr("r", 4)
        .attr("fill", s.color)
        .attr("cx", 0)
        .attr("cy", 0);

      legendItem.append("line")
        .attr("x1", -8)
        .attr("x2", 8)
        .attr("y1", 0)
        .attr("y2", 0)
        .attr("stroke", s.color)
        .attr("stroke-width", 2);

      legendItem.append("text")
        .attr("x", 12)
        .attr("y", 4)
        .attr("fill", "var(--text-default)")
        .attr("font-size", "1rem")
        .text(s.label);
    });
  }

  useEffect(() => {
    draw();
  }, [series, width, height])

  return <svg ref={svgRef} style={{ userSelect: 'none', width: '100%', height: '100%' }} />;
}
